import { useCallback, useEffect } from "react";

import useAssistantChat from "@api/assistant/aiCreateJob";
import { Box } from "@mui/material";

import { useAppSelector, useAppDispatch } from "@app/hooks";
import { store } from "@app/store";

import { setStreamJob } from "@features/jobManagement/slice";
import { addMessage } from "@features/messages/slice";

import { useGawainAssistant } from "@contexts/GawainAssistantContext";

import AssistantInput from "./AssistantInput";
import HistoryChat from "./HistoryChat";
import { AssistantMessageType } from "./type";

const CurrentChat = () => {
  const { mutateAsync: sendMessage } = useAssistantChat();
  const { user } = useAppSelector((state) => state.auth.userInfo);
  const {
    draft,
    setDraft,
    currentSession: conversationId,
    setGawainTicketRes,
    postJobFormData,
  } = useGawainAssistant();
  const {
    title,
    description,
    detailOfWork,
    budget,
    dueDate,
    priority,
    visibility,
    files,
    skills,
  } = postJobFormData;

  const dispatch = useAppDispatch();

  const handleAddNewMessage = useCallback(
    async ({
      text,
      imageUrls,
      fileUrls,
    }: {
      text: string;
      imageUrls: { name: string; url: string }[];
      fileUrls: { name: string; url: string }[];
    }) => {
      try {
        if (!user || (!text && !imageUrls.length && !fileUrls.length)) {
          return;
        }
        const jobFields = store.getState()?.jobManagement.fields;
        const newMessage = {
          text,
          userName: user.name,
          avatar: user.avatar,
          userId: user.id,
          fields: {
            ...jobFields,
            overview: jobFields?.description,
            skills: jobFields?.skills?.map((skill) => skill.text || skill.label),
          },
          imageUrls,
          fileUrls,
          conversationId,
        };
        try {
          const messageForRedux: AssistantMessageType = {
            id: Date.now().toString(), // temporary id
            title: user.name,
            content: text,
            avatar: user.avatar,
            userId: user.id,
            date: Date.now(),
            fileUrls,
            imageUrls,
          };
          dispatch(addMessage({ conversationId, message: messageForRedux }));
          dispatch(setStreamJob(true));
          const response = await sendMessage(newMessage);
        } catch (error) {
          console.error("Error sending message via mutation:", error);
        }
      } catch (error) {
        console.error("Error sending message or processing stream:", error);
      }
    },
    [
      user,
      title,
      description,
      detailOfWork,
      budget,
      dueDate,
      priority,
      visibility,
      files,
      skills,
      conversationId,
      sendMessage,
      setGawainTicketRes,
    ]
  );

  useEffect(() => {
    if (!draft) {
      return;
    }

    const { text, imageUrls, fileUrls } = draft;
    if (text || imageUrls.length || fileUrls.length) {
      handleAddNewMessage({ text, imageUrls, fileUrls });
      setDraft(null);
    }
  }, [handleAddNewMessage, draft]);

  return (
    <Box
      width={"100%"}
      height={"100%"}
      flex={1}
      display="flex"
      flexDirection="column"
      overflow={"hidden"}
      borderRadius={2}
      sx={{
        "&.css-dev-only-make-styles-popper": {
          position: "fixed",
        },
      }}
    >
      <Box
        flex={1}
        height={"100%"}
        overflow={"auto"}
        sx={{ "&::-webkit-scrollbar": { display: "none" } }}
      >
        <HistoryChat />
      </Box>
      <Box id="chat-editor-area" p={2}>
        <AssistantInput onSubmit={handleAddNewMessage} />
      </Box>
    </Box>
  );
};

export default CurrentChat;
